import { CloudOff, GroupOff, Schedule, Warning } from '@tempo-io/sui-icons';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@tempo-io/sui-tooltip';
import { useTranslation } from 'react-i18next';
import type { ActualCostCompletenessStatus } from '@/common/api/types/itemFinancials';

type NonCompleteStatus = Exclude<ActualCostCompletenessStatus, 'COMPLETE'>;

const STATUS_ICON: Record<NonCompleteStatus, { Icon: typeof Warning; colorClassName: string }> = {
  INCOMPLETE: { Icon: Warning, colorClassName: 'text-icon-warning' },
  MISSING_TEAM: { Icon: GroupOff, colorClassName: 'text-icon-warning' },
  PRESERVED: { Icon: Schedule, colorClassName: 'text-icon-info' },
  UNAVAILABLE: { Icon: CloudOff, colorClassName: 'text-icon-error' },
};

export const ActualCostStatusIndicator = ({
  status,
  testId = 'actual-cost-status-indicator',
}: {
  status: ActualCostCompletenessStatus | null | undefined;
  testId?: string;
}) => {
  const { t } = useTranslation();
  if (status == null || status === 'COMPLETE') return null;

  const { Icon, colorClassName } = STATUS_ICON[status];
  let label: string;
  switch (status) {
    case 'INCOMPLETE':
      label = t(($) => $.financials.actualCostStatus.incomplete);
      break;
    case 'MISSING_TEAM':
      label = t(($) => $.financials.actualCostStatus.missingTeam);
      break;
    case 'PRESERVED':
      label = t(($) => $.financials.actualCostStatus.preserved);
      break;
    case 'UNAVAILABLE':
      label = t(($) => $.financials.actualCostStatus.unavailable);
      break;
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label={label}
            className="inline-flex shrink-0 items-center justify-center"
            data-testid={testId}
          >
            <Icon aria-hidden="true" className={`size-3.5 ${colorClassName}`} />
          </button>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">{label}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
